import React, { useState, useMemo, useEffect } from 'react';
import { EMAIL_REGEX } from '@medflex/shared-utils';
import PropTypes from 'prop-types';
import Modal from '@medflex/components/stories/Modal';
import StaffMemberEdit from '@medflex/components/stories/StaffMemberEdit';
import hasUmlauts from '@medflex/utils/hasUmlauts';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { trackEvent } from '@medflex/utils/Tracking';
import { STAFF_TYPES } from '@medflex/utils/constants';
import { TITLE_OPTIONS, NO_TITLE } from '@medflex/utils/physicianStaffTitles';
import useStaff from '../hooks/useStaff';
import useModal from '../hooks/useModal';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';

const Edit = ({ id, type, onSubmit, isLoading, isCreating }) => {
  const { t } = useTranslation('global');
  const { staff } = useStaff();

  const staffMember = useMemo(() => {
    if (staff && id) {
      return staff.find(m => m.id === id);
    }
    return null;
  }, [id, staff]);

  const schema = yup.object().shape({
    type: yup.mixed().oneOf([STAFF_TYPES.PHYSICIAN, STAFF_TYPES.ASSISTANT]),
    title: yup.mixed().oneOf(TITLE_OPTIONS),
    firstName: yup.string().trim().required(t('validation.messages.required')),
    lastName: yup.string().trim().required(t('validation.messages.required')),
    email: yup
      .string()
      .trim()
      .test('', t('global:validation.messages.emailUmlauts'), value => !hasUmlauts(value))
      .matches(EMAIL_REGEX, t('global:validation.messages.email'))
      .required(t('validation.messages.required')),
    jobTitle: yup.string().trim(),
    patientAccessGranted: yup.bool().required(),
    physicianAccessGranted: yup.bool().required(),
  });

  const handleCheckBoxAction = event => {
    trackEvent(event, {
      zone: 'StaffMemberEdit | form',
      actionText: `${event.target.id} ${event.target.checked}`,
    });
  };

  return (
    <StaffMemberEdit
      initialValues={{
        type,
        title:
          (staffMember && staffMember.title) ||
          t('components:staff_member_form.name_title.titles.0'),
        firstName: (staffMember && staffMember.firstName) || '',
        lastName: (staffMember && staffMember.lastName) || '',
        email: (staffMember && staffMember.email) || '',
        jobTitle: (staffMember && staffMember.jobTitle) || '',
        patientAccessGranted: staffMember ? !!staffMember.patientAccessGrantedAt || false : true,
        physicianAccessGranted: staffMember
          ? !!staffMember.physicianAccessGrantedAt || false
          : true,
      }}
      onSubmit={onSubmit}
      validationSchema={schema}
      state={isLoading ? 'loading' : 'composing'}
      isCreating={isCreating}
      onCheckBoxAction={handleCheckBoxAction}
    />
  );
};

Edit.propTypes = {
  id: PropTypes.string,
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
  isCreating: PropTypes.bool,
  type: PropTypes.oneOf([STAFF_TYPES.PHYSICIAN, STAFF_TYPES.ASSISTANT]),
};

const PhysicianStaffMemberEditModule = () => {
  const { close } = useModal();
  const { updateStaffMember, createStaffMember, errors } = useStaff();
  const { t } = useTranslation('global');
  const { id, subType } = useLocationSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const isCreating = useMemo(() => typeof id === 'undefined', [id]);

  const handleSubmit = async (values, event) => {
    if (isLoading) return;
    setIsLoading(true);
    const payload = { ...values, type: subType };
    if (payload.title && payload.title === NO_TITLE) {
      payload.title = null;
    }
    if (payload.jobTitle === '') {
      payload.jobTitle = null;
    }
    try {
      if (isCreating) {
        trackEvent(event, { actionText: 'create' });
        await createStaffMember({ payload });
      } else {
        payload.id = id;
        const { email, type, ...rest } = payload;
        await updateStaffMember({ payload: rest });
        trackEvent(event, { actionText: 'edit' });
      }
      showSuccessToast(t('physician:StaffMemberEditModule.Form.success', ''), null, {
        toastId: 'staffMemberEditSuccess',
      });
      close({ modal: 'staff_member', id, subType });
    } catch (e) {
      trackEvent(event, { actionText: 'create failed' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const { create: error } = errors || {};
    if (!isLoading && error) {
      switch (error.response.status) {
        case 409:
          showAlertToast(
            t('physician:StaffMemberEditModule.Form.failure'),
            t('physician:StaffMemberEditModule.Form.failure_text_conflict'),
            {
              toastId: 'staffMemberEditFailedConflict',
            },
          );
          break;
        default:
          showAlertToast(
            t('physician:StaffMemberEditModule.Form.failure'),
            t('physician:StaffMemberEditModule.Form.failure_text'),
            {
              toastId: 'staffMemberEditFailed',
            },
          );
      }
    }
  }, [isLoading, errors]);

  const handleClose = event => {
    trackEvent(event, { zone: 'StaffMemberEdit | form', actionText: 'close' });
    close({ modal: 'staff_member', id, subType });
  };

  return (
    <Modal
      infoUrl="https://medflex.de/hilfe/behandler-starten/zugaenge-fuer-team-mitglieder-mitarbeiterinnen/"
      title={
        subType === STAFF_TYPES.PHYSICIAN
          ? t('components:staff_member_form.title_physician')
          : t('components:staff_member_form.title_assistant')
      }
      showCloseButton
      onClickCloseButton={handleClose}
    >
      <Edit
        id={id}
        type={subType}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        isCreating={isCreating}
      />
    </Modal>
  );
};

export default PhysicianStaffMemberEditModule;
